import React, { useState, useEffect } from 'react';
import { styled, useTheme, keyframes } from '@mui/material/styles';
import {
  Paper, Box, Grid, Typography, IconButton, Divider,
  Alert, AlertTitle, MobileStepper, Button, Stepper, Step, StepLabel, StepConnector, stepConnectorClasses
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SummarizeIcon from '@mui/icons-material/Summarize';
import InfoIcon from '@mui/icons-material/Info';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import ChanceStatusIcon from './ChanceStatusIcon';
import { getNumberComma } from '../../../utils/numberUtils';
import PlayArrowIcon from '@mui/icons-material/PlayArrow'; 
// --- 애니메이션 정의 ---
const arrowFlow = keyframes`
  0% { transform: translateX(-10px); opacity: 0.4; }
  50% { transform: translateX(10px); opacity: 1; }
  100% { transform: translateX(-10px); opacity: 0.4; }
`;

// --- 스타일 컴포넌트 ---
const WhiteIconButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.background.paper,
  '&:hover': { backgroundColor: theme.palette.getContrastText(theme.palette.background.paper) },
}));

const WhiteTypography = styled(Typography)(({ theme }) => ({
  color: theme.palette.background.paper,
}));

const ProjectInfoItem = ({ label, value, unit = '' }) => (
    <Box sx={{ 
    display: 'flex', 
    alignItems: 'center', 
    borderBottom: '1px dotted #e0e0e0', // 보고서 느낌의 하단 점선
    pb: 0.5,
    mr: 2
  }}>
    <Typography sx={{ 
      width: 100,           // 라벨 너비를 고정하여 값들의 시작 위치를 맞춤
      fontWeight: 'bold', 
      fontSize: '0.85rem', 
      color: 'text.secondary',
      flexShrink: 0 
    }}>
      {label}
    </Typography>
    <Typography sx={{ 
      fontSize: '0.9rem', 
      fontWeight: '500', 
      color: 'text.primary',
      whiteSpace: 'nowrap'  // 글자가 아래로 떨어지는 것 방지
    }}>
      {value || '-'}{unit}
    </Typography>
  </Box>
);

// --- 메인 컴포넌트 ---
const ChanceInfo = ({ chance, estimate, orderCount, handleGoBack }) => {
  const theme = useTheme();
  const [activeStep, setActiveStep] = useState(0);
  const [checkEstimate, setCheckEstimate] = useState(false);
  const [checkOrder, setCheckOrder] = useState(false);

  const steps = [
    { value: 0, label: '기회 확인', color: 'info' },
    { value: 1, label: '기회분석', color: 'error' },
    { value: 2, label: '견적', color: 'success' },
    { value: 3, label: '수주품의', color: 'secondary' },
  ];

  useEffect(() => {
    if (chance?.cstGrade) setActiveStep(chance.cstGrade - 1);
    setCheckEstimate(!!estimate);
    setCheckOrder(orderCount > 0);
  }, [chance, estimate, orderCount]);

  const handleNext = () => setActiveStep((prev) => prev + 1);
  const handleBack = () => setActiveStep((prev) => prev - 1);

  const currentStepConfig = steps[activeStep] || steps;

  return (
    <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', mb: 2, overflow: 'hidden' }}>
    {/* 1. 헤더 (아이콘까지 강제 축소) */}
    <Box sx={{ display: 'flex', alignItems: 'center', backgroundColor: '#424242', px: 1, minHeight: 42 }}> 
      <Box sx={{ p: 0.2 }}>
        <WhiteIconButton size="small" onClick={handleGoBack} sx={{ p: 0.5 }}>
          <ArrowBackIcon sx={{ fontSize: '1.1rem' }} /> {/* 화살표 크기 미세 조정 */}
        </WhiteIconButton>
      </Box>
      <Box sx={{ py: 0, display: 'flex', alignItems: 'center' }}> 
        {/* 아이콘 강제 축소: scale과 transform-origin으로 크기 조절 */}
        <Box sx={{ display: 'flex', transform: 'scale(0.85)', transformOrigin: 'left center' }}>
          <ChanceStatusIcon chance={chance} />
        </Box>
        <Box sx={{ ml: 0.5 }}> {/* 간격도 살짝 축소 */}
          <WhiteTypography 
            variant="body2" 
            sx={{ 
              color: 'white',
              fontWeight: 'bold',
              fontSize: '1rem', // 글자 크기 한 번 더 축소
              lineHeight: 1
            }}
          >
            현재 단계: {chance?.cstStatus || '-'}
          </WhiteTypography>
        </Box>
      </Box>
    </Box>
     {/* 2. 프로젝트 정보 영역 (간격 최적화) */}
      <Box sx={{ p: 2, bgcolor: 'background.paper' }}>
        {/* 상단 섹션 타이틀 */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5, px: 1 }}>
          <SummarizeIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>프로젝트 정보</Typography>
        </Box>
        <Divider sx={{ mb: 1, mx: 1 }} />
        
        <Box sx={{ px: 1, display: 'flex', flexDirection: 'column' }}>

          {/* 사업명 영역: mt(상단 여백)를 추가하여 구분선과 간격을 띄움 */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            mt: 2,         // 사업명 위쪽 간격 추가
            pb: 1,         // 하단 선과의 간격
            mb: 1.5,       // 아래 그리드와의 간격
            borderBottom: '1px solid', 
            borderColor: 'divider' 
          }}>
            <Typography sx={{ width: 100, fontWeight: 'bold', color: 'text.secondary', fontSize: '0.85rem' }}>
              사업명
            </Typography>
            <Typography sx={{ fontWeight: 'bold',  fontSize: '0.85rem' }}>
              {chance?.cstNm || '사업명 정보 없음'}
            </Typography>
          </Box>

          {/* 상세 정보 (4x2 그리드 레이아웃) */}
          <Grid container rowSpacing={1.5} columnSpacing={2}>
            <Grid item xs={3}><ProjectInfoItem label="매출처명" value={chance?.vendorCmpNm} /></Grid>
            <Grid item xs={3}><ProjectInfoItem label="매출 예상일" value={chance?.expDay} /></Grid>
            <Grid item xs={3}><ProjectInfoItem label="예상 금액" value={getNumberComma(chance?.expSales)} unit="원" /></Grid>
            <Grid item xs={3}><ProjectInfoItem label="예상 이익률" value={chance?.expBenefitRate} unit="%" /></Grid>

            <Grid item xs={3}><ProjectInfoItem label="원가 산출 여부" value={chance?.costYn === 'Y' ? '확정' : '미확정'} /></Grid>
            <Grid item xs={3}><ProjectInfoItem label="프로젝트 번호" value={chance?.prjNo} /></Grid>
            
            {/* 4x2 형태 유지를 위한 숨김 처리 */}
            <Grid item xs={3} sx={{ visibility: 'hidden' }}><ProjectInfoItem label="-" value="-" /></Grid>
            <Grid item xs={3} sx={{ visibility: 'hidden' }}><ProjectInfoItem label="-" value="-" /></Grid>

            {/*
              <InfoItem label="무상유지보수" value={chance?.mtnFreeSt && chance?.mtnFreeEd ? `${chance.mtnFreeSt} ~ ${chance.mtnFreeEd}` : '-'} />
            <InfoItem label="유상유지보수" value={chance?.mtnPaySt && chance?.mtnPayEd ? `${chance.mtnPaySt} ~ ${chance.mtnPayEd}` : '-'} />
            */}
          </Grid>
        </Box>
      </Box>
      {/* 3. 가이드 영역 */}
      <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, px: 1 }}>
          <InfoIcon fontSize="small" sx={{ mr: 1, color: `${currentStepConfig.color}.main` }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
            프로젝트 가이드 : {currentStepConfig.label}
          </Typography>
        </Box>
        <Divider sx={{ mb: 1.5, mx: 1 }} />
        <Box sx={{ px: 1, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
          {/* 가이드 지침 */}
          <Box sx={{ flex: 1 }}>
            {activeStep === 0 && <Alert severity="info" variant="outlined"><AlertTitle sx={{ fontWeight: 'bold' }}>기회확인</AlertTitle>기본사항 작성 및 영업활동을 입력하세요.</Alert>}
            {activeStep === 1 && <Alert severity="error" variant="outlined"><AlertTitle sx={{ fontWeight: 'bold' }}>기회분석</AlertTitle>요구사항 확인 및 원가 산출을 확정하세요.</Alert>}
            {activeStep === 2 && <Alert severity="success" variant="outlined"><AlertTitle sx={{ fontWeight: 'bold' }}>견적</AlertTitle>견적서를 작성하세요.</Alert>}
            {activeStep === 3 && <Alert severity="info" color="secondary" variant="outlined"><AlertTitle sx={{ fontWeight: 'bold' }}>수주품의</AlertTitle>수주품의서를 최종 확인하세요.</Alert>}
          </Box>
          
          {/* 체크리스트 박스 (타이틀 아래 한 줄 배치) */}
          <Box sx={{ flex: 1, py: 1.5, px: 2, backgroundColor: '#f9f9f9', borderRadius: 1 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1, fontWeight: 'bold' }}>
              체크리스트
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2, flexWrap: 'nowrap', overflowX: 'auto' }}>
              {activeStep === 0 && (
                <>
                  <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }} color={chance?.cstNm ? "primary" : "error"}>{chance?.cstNm ? "✅ 기본사항 입력 완료" : "· 기본 사항 미입력"}</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }} color={chance?.prjTyp ? "primary" : "error"}>{chance?.prjTyp ? "✅ 매출타입" : "· 매출타입 미선택"}</Typography>
                </>
              )}
              {activeStep === 1 && (
                <>
                  <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }} color={chance?.vendorCmp ? "primary" : "error"}>{chance?.vendorCmp ? "✅ 매출처" : "· 매출처 미선택"}</Typography>
                  {chance?.costYn !== 'Y' ? (
                    <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }} color="error">· 원가 미확정</Typography>
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, whiteSpace: 'nowrap' }}>
                      <Typography variant="body2" color="primary" sx={{ fontWeight: 'bold' }}>✅ 원가 확정</Typography>
                      <Typography variant="body2" color="primary" sx={{ fontSize: '0.85rem' }}><PlayArrowIcon sx={{ fontSize: '1rem', mx: 0.5 }} />  <b>매출 견적</b> 버튼 클릭</Typography>
                    </Box>
                  )}
                </>
              )}
              {activeStep === 2 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, whiteSpace: 'nowrap' }}>
                  {checkEstimate ? (
                    <>
                      <Typography variant="body2" color="primary" sx={{ fontWeight: 'bold' }}>
                        ✅ 견적 완료
                      </Typography>
                      <Typography variant="body2" color="primary" sx={{ display: 'flex', alignItems: 'center', fontSize: '0.85rem' }}>
                        <PlayArrowIcon sx={{ fontSize: '1rem', mx: 0.5 }} /> 
                        <b>수주 품의</b> 버튼을 눌러 진행하세요.
                      </Typography>
                    </>
                  ) : (
                    <Typography variant="body2" color="error">
                      · 견적서가 없습니다.
                    </Typography>
                  )}
                </Box>
              )}
              {activeStep === 3 && (
                <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }} color={checkOrder ? "primary" : "error"}>
                  {checkOrder ? "✅ 수주 품의 완료 (결재를 기다리고 있습니다.)" : "· 품의서를 작성하세요."}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* 5. 하단 내비게이션 (슬림 한 줄 스타일) */}
      <MobileStepper
        variant="dots"
        steps={steps.length}
        position="static"
        activeStep={activeStep}
        sx={{ 
          borderTop: 1, 
          borderColor: 'divider', 
          py: 0.5,
          minHeight: 40,
          bgcolor: 'transparent',
          '& .MuiMobileStepper-dot': { width: 6, height: 6, margin: '0 3px' }
        }}
        nextButton={
          <Button size="small" onClick={handleNext} disabled={activeStep === 3} sx={{ py: 0, fontSize: '0.8rem' }}>
            {steps[activeStep + 1]?.label} <KeyboardArrowRight fontSize="small" />
          </Button>
        }
        backButton={
          <Button size="small" onClick={handleBack} disabled={activeStep === 0} sx={{ py: 0, fontSize: '0.8rem' }}>
            <KeyboardArrowLeft fontSize="small" /> 뒤로
          </Button>
        }
      />
    </Paper>
  );
};

export default ChanceInfo;
